import { computed, nextTick, onScopeDispose, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';

import { useEventHub } from '@satellite/composables/useEventHub';
import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from './queryKeys';
import yardSpotAreaService from '@satellite/services/yardSpotArea-service';
import {
  EntityPresenterV2Mode,
  isEntityPresenterV2QueryEnabled,
  isLazyLiveMode,
  isStaticMode,
  subscribesToEventHub
} from './presenterV2EntityMode';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from './presenterV2Placeholder';
import type { YardAreaDetails } from './yardAreaPresenterV2Shared';

export interface UseYardAreaFetcherV2Options {
  /**
   * Area (spot area) ID to fetch. Can be a ref for reactivity.
   * Optional if initialAreaData (with id) is provided.
   */
  areaId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;

  /**
   * Optional initial area data used as initialData for the query.
   * Effective id is derived from areaId or initialAreaData.id (explicit areaId wins; mismatch triggers a warning).
   */
  initialAreaData?:
    | Ref<Maybe<YardAreaDetails>>
    | ComputedRef<Maybe<YardAreaDetails>>
    | YardAreaDetails;

  /**
   * Optional incomplete area data shown as placeholderData while the query fetches.
   */
  placeholderAreaData?:
    | Ref<Maybe<PresenterPlaceholderData<YardAreaDetails>>>
    | ComputedRef<Maybe<PresenterPlaceholderData<YardAreaDetails>>>
    | PresenterPlaceholderData<YardAreaDetails>;

  /**
   * When true, the query runs without viewport gating.
   * Default: false
   */
  headless?: boolean;

  /**
   * - `live`: fetches when enabled and subscribes to EventHub updates (default)
   * - `lazy-live`: no fetch until first matching EventHub event, refresh(), or invalidate()
   * - `static`: no fetch/subscriptions; returns staticAreaData only
   */
  mode?: EntityPresenterV2Mode;

  /**
   * Data source for `mode: 'static'`.
   */
  staticAreaData?:
    | Ref<Maybe<YardAreaDetails>>
    | ComputedRef<Maybe<YardAreaDetails>>
    | YardAreaDetails;

  staleTime?: number;

  onUpdateReceived?: (area: YardAreaDetails) => void;
}

export function useYardAreaFetcherV2(options: UseYardAreaFetcherV2Options) {
  const {
    areaId,
    initialAreaData,
    placeholderAreaData,
    headless = false,
    mode = EntityPresenterV2Mode.Live,
    staticAreaData,
    staleTime = Infinity,
    onUpdateReceived
  } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveAreaId = computed((): string | null => {
    const explicitId = unref(areaId);
    const fromObject = toValue(initialAreaData)?.id ?? null;

    if (explicitId) {
      if (fromObject && fromObject !== explicitId) {
        console.warn(
          '[useYardAreaFetcherV2] areaId mismatch:',
          `explicit areaId (${explicitId}) does not match area.id (${fromObject}). Using explicit areaId.`
        );
      }
      return explicitId;
    }
    return fromObject;
  });

  const initialArea = computed(() => toValue(initialAreaData) ?? undefined);
  const placeholderArea = computed(() => toValue(placeholderAreaData) ?? undefined);
  const staticArea = computed(() => toValue(staticAreaData) ?? undefined);

  if (
    isStaticMode(mode) &&
    (toValue(initialAreaData) != null || toValue(placeholderAreaData) != null)
  ) {
    console.warn(
      '[useYardAreaFetcherV2] mode=static ignores initialAreaData/placeholderAreaData. Use staticAreaData.'
    );
  }

  if (isLazyLiveMode(mode) && toValue(initialAreaData) != null) {
    console.warn(
      '[useYardAreaFetcherV2] mode=lazy-live does not use initialAreaData for the query cache; use placeholderAreaData for partial UI.'
    );
  }

  const lazyLiveUnlocked = ref(!isLazyLiveMode(mode));
  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);

  const wakeLazyLive = () => {
    if (isLazyLiveMode(mode) && !lazyLiveUnlocked.value) {
      lazyLiveUnlocked.value = true;
    }
  };

  const areaQuery = useQuery({
    queryKey: computed(() =>
      effectiveAreaId.value ? queryKeys.spotAreaDetail(effectiveAreaId.value) : []
    ),
    queryFn: async ({ signal }) => {
      if (!effectiveAreaId.value) {
        return null;
      }

      const response = await yardSpotAreaService.getOneByIdV2(effectiveAreaId.value, {
        requestConfig: { signal },
        enableCache: true
      });

      return response as YardAreaDetails;
    },
    enabled: computed(() =>
      isEntityPresenterV2QueryEnabled({
        mode,
        hasEffectiveId: Boolean(effectiveAreaId.value),
        isInViewport: isInViewport.value,
        lazyLiveUnlocked: lazyLiveUnlocked.value
      })
    ),
    initialData: computed(() =>
      isStaticMode(mode) || isLazyLiveMode(mode)
        ? undefined
        : initialArea.value
    ),
    placeholderData: computed(() => {
      if (isStaticMode(mode)) {
        return undefined;
      }
      return placeholderArea.value as YardAreaDetails | undefined;
    }),
    staleTime,
    refetchOnWindowFocus: false
  });

  const data = computed((): YardAreaDetails | null => {
    if (isStaticMode(mode)) {
      if (!staticArea.value) {
        console.warn('[useYardAreaFetcherV2] mode=static requires staticAreaData.');
        return null;
      }
      return staticArea.value;
    }
    return areaQuery.data.value || null;
  });

  const isLoading = computed(() =>
    isStaticMode(mode) ? false : areaQuery.isLoading.value
  );
  const isFetching = computed(() =>
    isStaticMode(mode) ? false : areaQuery.isFetching.value
  );
  const error = computed(() =>
    isStaticMode(mode) ? null : areaQuery.error.value
  );

  const invalidate = () => {
    if (isStaticMode(mode)) {
      return;
    }
    wakeLazyLive();
    if (effectiveAreaId.value) {
      queryClient.invalidateQueries(
        {
          queryKey: queryKeys.spotArea(effectiveAreaId.value)
        },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }
  };

  const refresh = async () => {
    if (isStaticMode(mode)) {
      return;
    }
    wakeLazyLive();
    await nextTick();
    await areaQuery.refetch();
  };

  const deferOrInvalidate = () => {
    wakeLazyLive();

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }

    const isIntersecting = typeof entry === 'boolean' ? entry : entry?.isIntersecting ?? true;
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const handleUpdateSpotArea = (event: { id?: string }) => {
    if (!event?.id || event.id !== effectiveAreaId.value) {
      return;
    }

    deferOrInvalidate();
  };

  watch(data, newArea => {
    if (onUpdateReceived && newArea) {
      onUpdateReceived(newArea);
    }
  });

  watch(effectiveAreaId, (newId, oldId) => {
    if (newId !== oldId) {
      pendingInvalidation.value = false;
      lazyLiveUnlocked.value = !isLazyLiveMode(mode);
    }
  });

  onMounted(() => {
    if (subscribesToEventHub(mode)) {
      eventHub.on('update-SpotArea', handleUpdateSpotArea);
    }
  });

  onScopeDispose(() => {
    if (subscribesToEventHub(mode)) {
      eventHub.off('update-SpotArea', handleUpdateSpotArea);
    }
  });

  return {
    data,
    isLoading,
    isFetching,
    error,
    refresh,
    invalidate,
    onIntersect: handleIntersect,
    query: areaQuery
  };
}
